/**
 * Invite people to a webinar: the rules both sides share. Designed in
 * `prototype/invite-people.html` (approved 19 Sep 2026): pick people, people
 * from other webinars by the tags Flashroom keeps there, people with the host's
 * own tags, everyone, or new people pasted in. Each person is counted once and
 * gets their own link.
 */
import { SYSTEM_TAGS, type SystemTag } from "./tags";

/** The most people one send may go to. */
export const INVITE_MAX = 5000;
/** The longest subject and note a host may write. */
export const INVITE_SUBJECT_MAX = 200;
export const INVITE_NOTE_MAX = 5000;

/**
 * Flashroom's own tags a host can pick people from another webinar by. Every
 * one except `unsubscribed`, because those people never get an invite, and the
 * ones that belong to a single session: a series is one webinar here, so a
 * person who came to any of its sessions came to it.
 */
export const INVITE_SYSTEM_TAGS: readonly SystemTag[] = SYSTEM_TAGS.filter(
  (t) => t.name !== "unsubscribed" && !t.perSession,
);

/** The tag a new person pasted into an invite gets on their contact. */
export const INVITED_TAG = "invited";

export interface PastedPerson {
  email: string;
  name: string | null;
}

interface PastedList {
  people: PastedPerson[];
  /** Lines with no address in them, as typed. */
  bad: string[];
}

const ADDRESS = /[^\s<>,;"'()[\]]+@[^\s<>,;"'()[\]]+\.[a-z]{2,}/gi;

function cleanName(raw: string): string | null {
  const name = raw
    .replace(/["'<>]/g, " ")
    .replace(/[\t,;:|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return name || null;
}

/**
 * What a host pasted: one person a line, as `Jo Park <jo@x.com>`, a bare
 * address, or a spreadsheet row with a name and an address. A line with several
 * addresses is several people. Addresses are lowercased and each is kept once.
 */
export function parsePasted(text: string): PastedList {
  const people: PastedPerson[] = [];
  const bad: string[] = [];
  const seen = new Set<string>();
  const keep = (email: string, name: string | null) => {
    const addr = email.toLowerCase();
    if (seen.has(addr)) return;
    seen.add(addr);
    people.push({ email: addr, name });
  };
  for (const raw of String(text ?? "").split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const bracketed = [...line.matchAll(/([^<>]*?)<\s*([^<>\s]+)\s*>/g)];
    if (bracketed.length > 0) {
      for (const m of bracketed) {
        const address = m[2].match(ADDRESS)?.[0];
        if (address) keep(address, cleanName(m[1]));
        else bad.push(m[0].trim());
      }
      continue;
    }
    const found = line.match(ADDRESS) ?? [];
    if (found.length === 0) {
      bad.push(line.slice(0, 120));
      continue;
    }
    // Why: one address on a line means the rest of the line is the name, the way a spreadsheet row pastes.
    const name = found.length === 1 ? cleanName(line.replace(found[0], " ")) : null;
    for (const address of found) keep(address, name);
  }
  return { people, bad };
}

/** First and last name from what an invite knows, for the form it fills in. */
export function splitName(name: string | null | undefined): { first: string; last: string } {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}

/** The email a host starts from, before AI Studio or they change a word. */
export function defaultInviteDraft(args: {
  title: string;
  /** "Thursday, October 1 · 7:00 PM ET" for a live, empty for a talk people watch any time. */
  when: string;
  /** "Thursday at 7:00 PM ET", beside the title in the subject. */
  whenShort: string;
  hostFirstName: string;
  /** A live that has ended: the invite is to watch its replay, and says no time. */
  replay?: boolean;
}): { subject: string; note: string } {
  const sign = args.hostFirstName.trim();
  const live = args.when.trim() !== "";
  if (args.replay) {
    const note = [
      "Hi {{first name}},",
      "I hosted {{room title}} live, and the replay is ready. I think you'll get a lot out of it, and you can watch it whenever you like.",
      "It's free, and it takes one tap.",
      sign,
    ]
      .filter(Boolean)
      .join("\n\n");
    return { subject: `Watch the replay: ${args.title}`, note };
  }
  const subject = live && args.whenShort ? `You're invited: ${args.title}, ${args.whenShort}` : `You're invited: ${args.title}`;
  const opening = live
    ? `I'm hosting {{room title}} live on ${args.when}, and I'd love for you to be there.`
    : "I put together {{room title}}, and I think you'll get a lot out of it. You can watch it whenever you like.";
  const note = ["Hi {{first name}},", opening, "It's free, and saving your seat takes one tap.", sign].filter(Boolean).join("\n\n");
  return { subject, note };
}

/** The note the page shows above a form it filled in from an invite. */
const INVITE_FILLED_NOTE = "We filled this in from your invite.";

/**
 * The script a register page runs when it opens from an invite link (`?i=`):
 * it asks for the invite, opens the person's seat if they already have one, and
 * otherwise fills the form in and carries the invite along with it. Nothing goes
 * inside the form: the note sits right before it and the invite rides in a hidden
 * field outside it, tied to the form by its `form` attribute. Plain ES5,
 * like the measurement runtime, so a model-designed page never has to know.
 */
export function inviteHead(slug: string): string {
  const path = JSON.stringify(`/api/w/${encodeURIComponent(slug)}/invite/`);
  const note = JSON.stringify(INVITE_FILLED_NOTE);
  return `<script data-invite>(function(){
  var m=/[?&]i=([A-Za-z0-9_-]{6,64})(&|$)/.exec(location.search||"");if(!m)return;var t=m[1],D=document;
  function set(f,n,v){var el=f.querySelector('[name="'+n+'"]');if(el&&v&&!el.value)el.value=v;}
  function hid(f,n){if(!f.id)f.id="fr-register";var all=D.getElementsByName(n);for(var j=0;j<all.length;j++)if(all[j].getAttribute("form")===f.id)return all[j];
    var h=D.createElement("input");h.type="hidden";h.name=n;h.setAttribute("form",f.id);(D.querySelector("[data-fr-door]")||D.body).appendChild(h);return h;}
  function fill(d){var fs=D.querySelectorAll("form[data-register]");for(var i=0;i<fs.length;i++){var f=fs[i];
    set(f,"first_name",d.first_name);set(f,"last_name",d.last_name);set(f,"name",(d.first_name+" "+d.last_name).replace(/^ +| +$/g,""));set(f,"email",d.email);
    hid(f,"invite").value=t;
    if(!f.previousElementSibling||!f.previousElementSibling.hasAttribute("data-invite-note")){var p=D.createElement("p");p.setAttribute("data-invite-note","");p.textContent=${note};p.style.cssText="margin:0 0 12px;font-size:.85em;opacity:.75";f.parentNode.insertBefore(p,f);}}}
  try{fetch(${path}+encodeURIComponent(t),{headers:{accept:"application/json"}}).then(function(r){return r.ok?r.json():null;}).then(function(d){if(!d)return;
    if(d.seat){location.replace(d.seat);return;}
    if(D.readyState==="loading")D.addEventListener("DOMContentLoaded",function(){fill(d);});else fill(d);}).catch(function(){});}catch(e){}
})();</script>`;
}
